import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types/FormLayoutType";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useDynamicPropertyLoadQuery, useDynamicPropertyUpdateMutation } from "../../services/server/DynamicPropertyApi";
import type { DynamicPropertyEntity } from "../../types/DynamicPropertyEntity";

export const DynamicPropertyEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.DYNAMIC_PROPERTIES}`);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("dynamic-property");

    const [formData, setFormData] = useState<DynamicPropertyEntity>({} as DynamicPropertyEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useDynamicPropertyLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useDynamicPropertyUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "DynamicProperty" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    const newFormData = {
        key: formData.keyProp,
        ...formData,
    };

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<DynamicPropertyEntity>
                    name="DynamicPropertyEditForm"
                    formData={newFormData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
